<script setup>
  import { onBeforeUnmount, onMounted, ref } from 'vue';

  import HomeButton from '@/components/home/HomeButton.vue';
  import HomeSectionHeading from '@/components/home/HomeSectionHeading.vue';

  import { HOME_HOW_IT_WORKS } from '@/constants/home';

  import stepArrow from '@/assets/images/home/step-dashed-arrow.svg';

  defineEmits(['get-price']);

  // numbers and arrows light up one after another once the row scrolls in (see v-reveal)
  const STEP_SEQUENCE_MS = 600;

  // Timings from the Figma prototype; animated only in the four-in-a-row layout (lg)
  const CARD_RISE_PX = 30;
  const CARD_MOVE_S = 1;
  const CARD_HOLD_S = 1;
  const CARD_WAITS_S = [0.6, 0.8, 1, 1.2];

  const cardElements = ref([]);
  let cardAnimations = [];
  let rowLayoutQuery = null;
  let reducedMotionQuery = null;

  const animateCard = (card, index) => {
    const wait = CARD_WAITS_S[index % CARD_WAITS_S.length];
    const cycle = wait + CARD_MOVE_S + CARD_HOLD_S + CARD_MOVE_S;
    const at = (seconds) => seconds / cycle;
    const raised = `0 -${CARD_RISE_PX}px`;

    return card.animate(
      [
        { translate: '0 0', offset: 0 },
        { translate: '0 0', offset: at(wait), easing: 'ease-in-out' },
        { translate: raised, offset: at(wait + CARD_MOVE_S) },
        { translate: raised, offset: at(wait + CARD_MOVE_S + CARD_HOLD_S), easing: 'ease-in-out' },
        { translate: '0 0', offset: 1 },
      ],
      { duration: cycle * 1000, iterations: Infinity }
    );
  };

  const stopCardAnimations = () => {
    cardAnimations.forEach((animation) => animation.cancel());
    cardAnimations = [];
  };

  const updateCardAnimations = () => {
    const shouldAnimate = rowLayoutQuery.matches && !reducedMotionQuery.matches;
    if (shouldAnimate && !cardAnimations.length) {
      cardAnimations = cardElements.value.map(animateCard);
    } else if (!shouldAnimate) {
      stopCardAnimations();
    }
  };

  onMounted(() => {
    rowLayoutQuery = window.matchMedia('(min-width: 1024px)');
    reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    rowLayoutQuery.addEventListener('change', updateCardAnimations);
    reducedMotionQuery.addEventListener('change', updateCardAnimations);
    updateCardAnimations();
  });

  onBeforeUnmount(() => {
    rowLayoutQuery.removeEventListener('change', updateCardAnimations);
    reducedMotionQuery.removeEventListener('change', updateCardAnimations);
    stopCardAnimations();
  });
</script>

<template>
  <section id="how-it-works" class="w-full scroll-mt-18 md:scroll-mt-21 lg:scroll-mt-25">
    <div class="from-blue-light to-bright-cyan bg-linear-to-b">
      <div
        class="mx-auto flex w-full max-w-299 flex-col items-center px-8 pt-20 pb-24 lg:pt-33 lg:pb-18 xl:px-0"
      >
        <HomeSectionHeading
          v-reveal
          inverted
          :eyebrow="HOME_HOW_IT_WORKS.eyebrow"
          :title="HOME_HOW_IT_WORKS.title"
        />

        <div
          v-reveal="100"
          class="mt-14 hidden w-full grid-cols-4 gap-5 lg:grid"
          aria-hidden="true"
        >
          <div
            v-for="(step, index) in HOME_HOW_IT_WORKS.steps"
            :key="step.title"
            class="relative flex justify-center"
          >
            <span
              class="step-number flex size-20 items-center justify-center rounded-full border-4 border-white text-[42px] font-bold text-white"
              :style="{ '--step-delay': `${index * STEP_SEQUENCE_MS}ms` }"
            >
              {{ index + 1 }}
            </span>
            <img
              v-if="index < HOME_HOW_IT_WORKS.steps.length - 1"
              :src="stepArrow"
              alt=""
              class="step-arrow absolute top-1/2 left-[calc(50%+66px)] w-[calc(100%+1.25rem-132px)] -translate-y-1/2"
              :style="{ '--step-delay': `${index * STEP_SEQUENCE_MS + STEP_SEQUENCE_MS / 2}ms` }"
            />
          </div>
        </div>

        <ol
          v-reveal="150"
          class="2xs:px-7 mt-12 grid w-full max-w-134 gap-6 px-2 md:max-w-none md:grid-cols-2 md:px-0 lg:mt-18 lg:grid-cols-4 lg:gap-5"
        >
          <li
            v-for="step in HOME_HOW_IT_WORKS.steps"
            :key="step.title"
            ref="cardElements"
            class="flex flex-col items-center rounded-[20px] bg-white px-4.5 py-9 text-center"
          >
            <div class="flex h-14 items-center">
              <img :src="step.icon" alt="" />
            </div>
            <h3
              class="text-cobalt mt-6 max-w-47 text-[26px] leading-[1.22] font-bold whitespace-pre-line lg:text-[22px] xl:text-[26px]"
            >
              {{ step.title }}
            </h3>
            <p
              class="text-ink mt-6 text-[17px]/6 font-medium tracking-[-0.005em] lg:text-[15px]/5.5 xl:text-[17px]/6"
            >
              {{ step.text }}
            </p>
          </li>
        </ol>

        <HomeButton variant="outline" class="mt-14" @click="$emit('get-price')">
          {{ HOME_HOW_IT_WORKS.cta }}
        </HomeButton>
      </div>
    </div>

    <div
      class="bg-bright-cyan -mt-px h-[max(48px,5.55vw)] [clip-path:polygon(0_0,100%_0,50%_100%)]"
      aria-hidden="true"
    />
  </section>
</template>

<style scoped>
  .reveal:not(.is-revealed) .step-number {
    opacity: 0;
  }

  .reveal:not(.is-revealed) .step-arrow {
    clip-path: inset(0 100% 0 0);
  }

  .is-revealed .step-number {
    animation: step-number-pop 0.4s ease-out var(--step-delay) both;
  }

  .is-revealed .step-arrow {
    animation: step-arrow-draw 0.5s ease-in-out var(--step-delay) both;
  }

  @keyframes step-number-pop {
    from {
      opacity: 0;
      transform: scale(0.6);
    }
  }

  @keyframes step-arrow-draw {
    from {
      clip-path: inset(0 100% 0 0);
    }
    to {
      clip-path: inset(0 0 0 0);
    }
  }
</style>
